/**
 * "Options as nodes": the current word's next-word options (the same
 * out-neighbours the word buttons offer) drawn on the board at their map
 * positions, as small muted candidate nodes with quiet spokes from the
 * current word. Shared look and the option list itself live here.
 */
import { create } from "zustand";

export const OPTION_R = 7;
export const OPTION_FILL = "#5A5F66";
/** Fill while the matching word button is hovered or focused. */
export const OPTION_FILL_HOVER = "#8A9099";
export const OPTION_OUTLINE = "#121212";
export const OPTION_LABEL_FONT = 12;
export const OPTION_LABEL_COLOR = "#A4A9B0";
export const SPOKE_WIDTH = 1.25;
export const SPOKE_COLOR = "#3A3F45";
/**
 * Options closer to the current word than this (px) are nudged out along
 * their own direction, so a tight cluster fans out into a readable ring.
 */
export const OPTION_MIN_SPOKE = 44;
/** Invisible touch target around a small option node, px. */
export const OPTION_HIT_R = 16;

/** Push points closer than `min` to `c` out to `min`, keeping direction. */
export function fanOut<T extends { px: number; py: number }>(
  c: { px: number; py: number },
  pts: T[],
  min = OPTION_MIN_SPOKE,
): T[] {
  return pts.map((p, i) => {
    const dx = p.px - c.px;
    const dy = p.py - c.py;
    const d = Math.hypot(dx, dy);
    if (d >= min) return p;
    const a = d < 1e-6 ? (i * 2.399963) % (2 * Math.PI) : Math.atan2(dy, dx);
    return { ...p, px: c.px + Math.cos(a) * min, py: c.py + Math.sin(a) * min };
  });
}

type Graph = Record<string, { edges?: Record<string, number> } | undefined>;

/**
 * The current word's out-neighbours, most similar first: exactly the words
 * the word buttons offer, in the same order.
 */
export function optionWords(
  graph: Graph | null | undefined,
  current: string | null | undefined,
): string[] {
  if (!graph || !current) return [];
  const edges = graph[current]?.edges ?? {};
  return Object.entries(edges)
    .sort((a, b) => b[1] - a[1])
    .map(([w]) => w);
}

/** Which option is hovered or focused, on the board or among the buttons. */
interface OptionHoverState {
  hovered: string | null;
  setHovered: (word: string | null) => void;
  /** Clears only if `word` is still the hovered one. */
  clear: (word: string) => void;
}

export const useOptionHover = create<OptionHoverState>((set, get) => ({
  hovered: null,
  setHovered: (word) => set({ hovered: word }),
  clear: (word) => {
    if (get().hovered === word) set({ hovered: null });
  },
}));
